import { z } from "zod";

// Copied from https://gist.github.com/TonyGravagno/2b744ceb99e415c4b53e8b35b309c29c

/**
 * @summary Function returns default object from Zod schema
 * @version 23.05.15.2
 * @link https://gist.github.com/TonyGravagno/2b744ceb99e415c4b53e8b35b309c29c
 * @author Jacob Weisenburger, Josh Andromidas, Thomas Moiluiavon, Tony Gravagno
 * @param schema z.object schema definition
 * @param options Optional object, see Example for details
 * @returns Object of type schema with defaults for all fields
 * @example
 * const schema = z.object( { ... } )
 * const default1 = getZodDefaults<typeof schema>(schema)
 * const default2 = getZodDefaults<typeof schema>(
 *   schema,{ // toggle from these defaults if required
 *     defaultArrayEmpty: false,
 *     defaultDateEmpty: false,
 *     defaultDateUndefined: false,
 *     defaultDateNull: false,
 * } )
 */
export function getZodDefaults<T extends z.ZodTypeAny>(
  schema: z.ZodObject | z.ZodPipe<any, any>,
  options: object = {},
): z.infer<T> | null {
  const defaultArrayEmpty =
    "defaultArrayEmpty" in options ? options.defaultArrayEmpty : false;
  const defaultDateEmpty =
    "defaultDateEmpty" in options ? options.defaultDateEmpty : false;
  const defaultDateUndefined =
    "defaultDateUndefined" in options ? options.defaultDateUndefined : false;
  const defaultDateNull =
    "defaultDateNull" in options ? options.defaultDateNull : false;

  function run(): z.infer<T> | null {
    if (schema instanceof z.ZodPipe) {
      const inner = schema.def.in as z.ZodTypeAny;
      if (inner instanceof z.ZodPipe) {
        return getZodDefaults(inner, options); // recursive ZodPipe
      }
      // return schema inner shape as a fresh zodObject
      return getZodDefaults(z.object((inner as z.ZodObject).shape), options);
    }

    if (schema instanceof z.ZodType) {
      const the_shape = (schema as z.ZodObject).shape as z.ZodAny; // eliminates 'undefined' issue
      const entries = Object.entries(the_shape);
      const temp = entries.map(([key, value]) => {
        const this_default =
          value instanceof z.ZodPipe
            ? getZodDefaults(value, options)
            : getDefaultValue(value);
        return [key, this_default];
      });
      return Object.fromEntries(temp);
    } else {
      console.log(`Error: Unable to process this schema`);
      return null; // unknown or undefined here results in complications
    }

    function getDefaultValue(dschema: z.ZodTypeAny): any {
      if (dschema instanceof z.ZodDefault) {
        if (!("def" in dschema)) {
          return undefined;
        } // error
        if (!("defaultValue" in dschema.def)) {
          return undefined;
        } // error
        return dschema.def.defaultValue;
      }
      if (dschema instanceof z.ZodArray) {
        // return empty array or array with one empty typed element
        return defaultArrayEmpty
          ? []
          : [getDefaultValue(dschema.element as z.ZodTypeAny)];
      }
      if (dschema instanceof z.ZodOptional) {
        return undefined;
      }
      if (dschema instanceof z.ZodString) {
        return "";
      }
      if (dschema instanceof z.ZodNumber || dschema instanceof z.ZodBigInt) {
        const value = dschema.minValue ?? 0;
        return value;
      }
      if (dschema instanceof z.ZodDate) {
        const value = defaultDateEmpty
          ? ""
          : defaultDateNull
            ? null
            : defaultDateUndefined
              ? undefined
              : dschema.minDate;
        return value;
      }
      if (dschema instanceof z.ZodSymbol) {
        return "";
      }
      if (dschema instanceof z.ZodBoolean) {
        return false;
      }
      if (dschema instanceof z.ZodNull) {
        return null;
      }
      if (dschema instanceof z.ZodPipe) {
        if (!("out" in dschema.def)) {
          return undefined;
        } // error

        return getDefaultValue(dschema.def.out as z.ZodTypeAny);
      }
      if (dschema instanceof z.ZodObject) {
        return getZodDefaults(dschema, options);
      }
      if (dschema instanceof z.ZodAny && !("innerType" in dschema.def)) {
        return undefined;
      } // error?

      return getDefaultValue((dschema.def as any).innerType);
    }
  }
  return run();
}
